import { useEffect, useState } from 'react'
import {
	getAwardsListLevelCount,
	getAwardsListRankingCount,
	getAwardsListGradeLevelCount,
	getOverviewTexts,
	saveOverviewTexts
} from '../../../lib/studentawardsreport'
import style from './globalstudentcourselessonreport.module.css'
import { Toast, ToastContainer } from 'react-bootstrap'

const RecognitionTable = ({ recognition, overview, onOverviewChange }) => {
	const total = recognition.reduce((sum, r) => sum + (r.Count || 0), 0)
	const rowCount = recognition.length
	return (
		<>
			<div className={style.sectionHeader}>RECOGNITION</div>
			<table className="table table-bordered text-center" style={{ marginBottom: 0 }}>
				<colgroup>
					<col style={{ width: '30%' }} />
					<col style={{ width: '20%' }} />
					<col style={{ width: '50%' }} />
				</colgroup>
				<thead>
					<tr>
						<th className={style.tableHeader}>Level</th>
						<th className={style.tableHeader}>Count</th>
						<th className={style.tableHeader}>Overview</th>
					</tr>
				</thead>
				<tbody>
					{recognition.map((r, index) => (
						<tr key={`recognition-${r.Level}`}>
							<td>{r.Level}</td>
							<td className={r.Color}>{r.Count}</td>
							{index === 0 && (
								<td rowSpan={rowCount + 1} className={style.overviewCell}>
									<div className="h-100 w-100">
										<textarea
											rows={rowCount * 2}
											className="form-control flex-grow-1"
											value={overview['recognition'] || ''}
											onChange={(e) => onOverviewChange('recognition', e.target.value)}
										/>
									</div>
								</td>
							)}
						</tr>
					))}
					<tr className={style.totalRow}>
						<td className={style.tableHeader}>Total</td>
						<td className={style.tableHeader}>{total}</td>
					</tr>
				</tbody>
			</table>
		</>
	)
}

const RankingTable = ({ ranking, overview, onOverviewChange }) => {
	const total = ranking.reduce((sum, r) => sum + (r.Count || 0), 0)
	const rowCount = ranking.length
	return (
		<>
			<div className={style.sectionHeader}>RANKING</div>
			<table className="table table-bordered text-center" style={{ marginBottom: 0 }}>
				<colgroup>
					<col style={{ width: '30%' }} />
					<col style={{ width: '20%' }} />
					<col style={{ width: '50%' }} />
				</colgroup>
				<thead>
					<tr>
						<th className={style.tableHeader}>Ranking</th>
						<th className={style.tableHeader}>Count</th>
						<th className={style.tableHeader}>Overview</th>
					</tr>
				</thead>
				<tbody>
					{ranking.map((r, index) => (
						<tr key={`ranking-${r.Ranking}`}>
							<td>{r.Ranking}</td>
							<td className={r.Color}>{r.Count}</td>
							{index === 0 && (
								<td rowSpan={rowCount + 1} className={style.overviewCell}>
									<div className="h-100 w-100">
										<textarea
											rows={rowCount * 2}
											className="form-control flex-grow-1"
											value={overview['ranking'] || ''}
											onChange={(e) => onOverviewChange('ranking', e.target.value)}
										/>
									</div>
								</td>
							)}
						</tr>
					))}
					<tr className={style.totalRow}>
						<td className={style.tableHeader}>Total</td>
						<td className={style.tableHeader}>{total}</td>
					</tr>
				</tbody>
			</table>
		</>
	)
}

const GradeLevelTable = ({ gradeLevel, overview, onOverviewChange }) => {
	const total = gradeLevel.reduce((sum, g) => sum + (g.Count || 0), 0)
	const rowCount = gradeLevel.length
	return (
		<>
			<div className={style.sectionHeader}>GRADE LEVEL</div>
			<table className="table table-bordered text-center" style={{ marginBottom: 0 }}>
				<colgroup>
					<col style={{ width: '30%' }} />
					<col style={{ width: '20%' }} />
					<col style={{ width: '50%' }} />
				</colgroup>
				<thead>
					<tr>
						<th className={style.tableHeader}>Year</th>
						<th className={style.tableHeader}>Count</th>
						<th className={style.tableHeader}>Overview</th>
					</tr>
				</thead>
				<tbody>
					{gradeLevel.map((g, index) => (
						<tr key={`gradeLevel-${g.Year}`}>
							<td>Grade-{g.Year}</td>
							<td className={g.Color}>{g.Count}</td>
							{index === 0 && (
								<td rowSpan={rowCount} className={style.overviewCell}>
									<div className="h-100 w-100">
										<textarea
											rows={rowCount * 2}
											className="form-control flex-grow-1"
											value={overview['gradeLevel'] || ''}
											onChange={(e) => onOverviewChange('gradeLevel', e.target.value)}
										/>
									</div>
								</td>
							)}
						</tr>
					))}
					<tr>
						<td className={style.tableHeader}>HS Total</td>
						<td className={style.tableHeader}>{total}</td>
					</tr>
				</tbody>
			</table>
		</>
	)
}

export default function GlobalStudentAwardsReportList({ studentId }) {
	const [recognition, setRecognition] = useState([])
	const [ranking, setRanking] = useState([])
	const [gradeLevel, setGradeLevel] = useState([])
	const [overview, setOverview] = useState([])
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const loadData = async () => {
		console.log('loadData called')
		const overviewRes = await getOverviewTexts(studentId)

		const loadRecognition = async () => {
			const recognitionRes = await getAwardsListLevelCount(studentId)
			const recognitionTypes = {
				International: style.studentActivityBlue,
				National: style.studentActivityGreen,
				Regional: style.studentActivityYellow,
				State: style.studentActivityOrange,
				City: style.studentActivityLightRed,
				School: style.studentActivityRed
			}

			// map each recognition type to its level
			const recognitionMap = recognitionRes.reduce((acc, item) => {
				const level = item.Level

				if (recognitionTypes[level]) {
					acc[level] = {
						Level: level,
						Count: item.Count,
						Color: recognitionTypes[level]
					}
				} else {
					acc[level] = {
						Level: level,
						Count: item.Count,
						Color: style.studentActivityDefault
					}
				}
				return acc
			}, {})

			setRecognition(Object.values(recognitionMap))
		}

		const loadRanking = async () => {
			const rankingRes = await getAwardsListRankingCount(studentId)
			const rankingTypes = {
				'1 or 2': style.studentActivityBlue,
				'3 or 4': style.studentActivityGreen,
				'5 or 6': style.studentActivityYellow,
				'7 or More': style.studentActivityOrange,
				Certificate: style.studentActivityLightRed,
				Participation: style.studentActivityRed
			}

			const rankingMap = rankingRes.reduce((acc, item) => {
				if (rankingTypes[item.Ranking]) {
					acc[item.Ranking] = {
						Ranking: item.Ranking,
						Count: item.Count,
						Color: rankingTypes[item.Ranking]
					}
				} else {
					// map it with default color if not found
					acc[item.Ranking] = {
						Ranking: item.Ranking,
						Count: item.Count,
						Color: style.studentActivityDefault
					}
				}
				return acc
			}, {})

			setRanking(Object.values(rankingMap))
		}

		const loadGradeLevel = async () => {
			const gradeLevelRes = await getAwardsListGradeLevelCount(studentId)
			const gradeLevelTypes = {
				7: style.studentActivityBlue,
				8: style.studentActivityGreen,
				9: style.studentActivityYellow,
				10: style.studentActivityOrange,
				11: style.studentActivityLightRed,
				12: style.studentActivityRed
			}

			const gradeLevelMap = gradeLevelRes.reduce((acc, item) => {
				if (gradeLevelTypes[item.Year]) {
					acc[item.Year] = {
						Year: item.Year,
						Count: item.Count,
						Color: gradeLevelTypes[item.Year]
					}
				} else {
					acc[item.Year] = {
						Year: item.Year,
						Count: item.Count,
						Color: style.studentActivityDefault
					}
				}
				return acc
			}, {})

			setGradeLevel(Object.values(gradeLevelMap))
		}

		await Promise.all([loadRecognition(), loadRanking(), loadGradeLevel()])

		const overviewMap = {}
		overviewRes.forEach((item) => {
			overviewMap[item.type] = item.desc
		})

		setOverview(overviewMap)
	}

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const handleOverviewChange = (key, value) => {
		console.log('handleOverviewChange called')

		setOverview((prev) => ({ ...prev, [key]: value }))
	}

	const handleUpdate = async () => {
		console.log('handleUpdate called')
		await saveOverviewTexts(studentId, overview)
		showToastMessage('Update successful!')
		await loadData()
	}

	useEffect(() => {
		console.log('useEffect called')

		loadData()
	}, [studentId])

	return (
		<div className="card p-3">
			<h4 className="mb-3">Awards Reports</h4>
			<ToastContainer style={{ bottom: '1rem', right: '1rem', position: 'fixed' }}>
				<Toast show={showToast} onClose={() => setShowToast(false)} delay={2000} autohide>
					<Toast.Header>
						<strong className="me-auto">Notification</strong>
					</Toast.Header>
					<Toast.Body>{toastMessage}</Toast.Body>
				</Toast>
			</ToastContainer>

			<RecognitionTable recognition={recognition} onOverviewChange={handleOverviewChange} overview={overview} />
			<RankingTable ranking={ranking} onOverviewChange={handleOverviewChange} overview={overview} />
			<GradeLevelTable gradeLevel={gradeLevel} onOverviewChange={handleOverviewChange} overview={overview} />

			<div className="text-max text-end">
				<button className="btn btn-primary" onClick={handleUpdate}>
					Update
				</button>
			</div>
		</div>
	)
}
